import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Box, Button, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { ArrowLeft, Trophy } from "lucide-react";
import { getPrivateChallenge, getPrivateChallengeLeaderBoard, formatTime } from "utils/supabase";
import BackdropAlert from "components/Custom/BackdropAlert.js";

const PODIUM_COLORS = {
  1: { bg: "#fef9c3", border: "#facc15", color: "#b45309" },
  2: { bg: "#f1f5f9", border: "#cbd5e1", color: "#475569" },
  3: { bg: "#fff7ed", border: "#fdba74", color: "#c2410c" },
};
const DEFAULT_POSITION_COLORS = { bg: "#ede9fe", border: "#ddd6fe", color: "#7c3aed" };

function PositionBadge({ position }) {
  const colors = PODIUM_COLORS[position] || DEFAULT_POSITION_COLORS;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
      <Typography sx={{ minWidth: 18, color: colors.color, fontSize: 13, fontWeight: 800, textAlign: "right" }}>
        {position}
      </Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "9999px",
          bgcolor: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Trophy size={16} />
      </Box>
    </Box>
  );
}

export default function RetoLeaderboard() {
  const { inviteCode } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [examId, setExamId] = useState(null);
  const [backdropAlert, setBackdropAlert] = useState({ open: false, severity: "info", title: "", message: "" });

  const closeBackdropAlert = () => setBackdropAlert((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    async function fetchLeaderboard() {
      setLoading(true);
      try {
        const { data, error } = await getPrivateChallengeLeaderBoard(inviteCode);
        if (error || !data?.success) {
          throw new Error(error?.message || "No se pudo cargar la tabla de posiciones.");
        }
        setChallenge(data.challenge);

        // El exam_id del usuario actual no viene en el leaderboard, lo obtenemos aparte.
        const { data: challengeData } = await getPrivateChallenge(inviteCode);
        if (challengeData?.success && challengeData.challenge?.exam_id) {
          setExamId(challengeData.challenge.exam_id);
        }
      } catch (error) {
        console.error("Error fetching private challenge leaderboard:", error);
        setBackdropAlert({
          open: true,
          severity: "error",
          title: "No se pudo cargar el reto",
          message: error?.message || "Ocurrio un error inesperado.",
        });
      } finally {
        setLoading(false);
      }
    }

    if (inviteCode) fetchLeaderboard();
  }, [inviteCode]);

  const leaderboard = challenge?.leaderboard || [];

  return (
    <div className="flex flex-wrap mt-100 justify-center">
      <div className="w-full lg:w-8/12 px-4">
        <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: "16px", p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              rowGap: 1,
            }}
          >
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={() => history.push("/admin/dashboard")}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 600, px: 0, minWidth: 0 }}
            >
              Volver
            </Button>

            {examId ? (
              <Button
                onClick={() => history.push(`/admin/simulador/${examId}/resultados`)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  color: "#7c3aed",
                  borderColor: "#c4b5fd",
                  "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
                }}
                variant="outlined"
              >
                Ver resultados
              </Button>
            ) : null}
          </Box>

          {loading ? (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: "10px" }} />
              ))}
            </Stack>
          ) : challenge ? (
            <>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#111827", mt: 1 }}>
                🏆 {challenge.title}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 2.5 }}>
                {challenge.completed} de {challenge.participants} participantes han finalizado
              </Typography>

              <Stack spacing={1.25}>
                {leaderboard.map((row) => (
                  <Box
                    key={row.user_id || row.position}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "64px minmax(0, 1fr) auto",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "12px",
                      border: row.is_current_user ? "1px solid #7c3aed" : "1px solid #e5e7eb",
                      bgcolor: row.is_current_user ? "#f5f3ff" : "#fff",
                    }}
                  >
                    <PositionBadge position={row.position} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.display_name}
                        {row.is_current_user ? " (Tú)" : ""}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                        {row.correct_answers}/{row.total_questions} correctas · {formatTime(row.time_seconds)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#7c3aed" }}>{row.score}%</Typography>
                  </Box>
                ))}

                {leaderboard.length === 0 ? (
                  <Typography sx={{ color: "#6b7280", textAlign: "center", py: 3 }}>
                    Aún no hay resultados para este reto.
                  </Typography>
                ) : null}
              </Stack>
            </>
          ) : null}
        </Paper>
      </div>

      <BackdropAlert
        open={backdropAlert.open}
        onClose={closeBackdropAlert}
        severity={backdropAlert.severity}
        title={backdropAlert.title}
        message={backdropAlert.message}
        primaryAction={{ label: "Cerrar", onClick: closeBackdropAlert }}
      />
    </div>
  );
}
